/**
 * A display-only price ordering for product-discovery results.
 *
 * Keep this separate from selection and persistence: callers receive a new
 * array, while each product/quote keeps its original object and ID. That lets
 * a purchaser compare the lowest priced discovered products first without
 * changing the selected listing or any row-specific manual controls.
 */
export function finitePositiveProductPrice(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) && value > 0 ? value : null;
  }
  if (typeof value !== 'string') return null;

  const trimmed = value.trim();
  if (!trimmed) return null;
  // Product-search sources commonly return display-formatted currency values
  // such as "$1,250.50". Do not try to guess at prose like "call for price";
  // those values deliberately remain unpriced and sort after priced results.
  const normalized = trimmed.replace(/[$,\s]/g, '');
  if (!/^[+]?\d+(?:\.\d+)?$/.test(normalized)) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

/** An explicit unknown catalog price must not fall back to a contract award. */
type PriceProduct = { price?: unknown; unit_price?: unknown; price_status?: string;
  price_region?: string | null;
  price_observations?: readonly Record<string, any>[]; configuration?: string | null;
  source_evidence?: readonly Record<string, any>[]; product_name?: string; model?: string;
  catalog_id?: string; price_provider_id?: string; evidence?: { source?: string; doc_type?: string; url?: string; provider_id?: string;
    catalog_id?: string; document_sha256?: string; configuration?: string | null };
  specifications?: { award_amount?: unknown } };

const finiteUnitAmount = (value: unknown): number | null => {
  if (typeof value === 'number') return Number.isFinite(value) && value >= 0 ? value : null;
  if (typeof value !== 'string' || !/^\$?(?:\d+|\d{1,3}(?:,\d{3})+)(?:\.\d+)?$/.test(value.trim())) return null;
  const amount = Number(value.trim().replace(/[$,]/g, ''));
  return Number.isFinite(amount) && amount >= 0 ? amount : null;
};

const unitObservations = (product: PriceProduct): Record<string, any>[] =>
  (product.price_observations || []).filter(row => row && row.verified === true
    && row.basis === 'unit' && typeof row.amount === 'number' && finiteUnitAmount(row.amount) !== null
    && typeof row.currency === 'string' && /^[A-Z]{3}$/.test(row.currency)
    && typeof row.unit === 'string' && Boolean(row.unit.trim())
    && typeof row.document_sha256 === 'string' && /^[a-f0-9]{64}$/i.test(row.document_sha256)
    && Number.isInteger(row.page) && row.page > 0 && typeof row.source_text === 'string' && Boolean(row.source_text.trim())
    && (!(product.configuration || product.evidence?.configuration) || !(row.configuration) || row.configuration === (product.configuration || product.evidence?.configuration))
    && (!(product.catalog_id || product.evidence?.catalog_id) || row.catalog_id === (product.catalog_id || product.evidence?.catalog_id))
    && (!product.evidence?.document_sha256 || row.document_sha256 === product.evidence.document_sha256));

const publishedListingZero = (product: PriceProduct): boolean => [product, ...(product.source_evidence || [])].some(row => {
  if (finiteUnitAmount(row.price) !== 0 || (row.model && product.model && row.model !== product.model)
    || (row.product_name && product.product_name && row.product_name !== product.product_name)
    || (row.configuration && product.configuration && row.configuration !== product.configuration)) return false;
  const evidence = row.evidence;
  if (evidence?.source !== 'gsa_advantage' || evidence.doc_type !== 'gsa_search_json') return false;
  try {
    const url = new URL(evidence.url || '');
    return url.protocol === 'https:' && url.hostname === 'www.gsaadvantage.gov'
      && url.pathname.endsWith('/catalog/product_detail') && Boolean(url.searchParams.get('gsin'));
  } catch { return false; }
});

export function productUnitPrice(product: PriceProduct): number | null {
  if (['quote_required', 'not_published', 'region_unresolved', 'historical', 'unknown'].includes(product.price_status || '')) return null;
  const value = product.price === undefined ? product.unit_price : product.price;
  const amount = finiteUnitAmount(value);
  const observations = unitObservations(product);
  if (!product.price_status && observations.length > 1
    && new Set(observations.map(row => `${row.amount}|${row.currency}|${row.unit}|${row.configuration || ''}`)).size > 1) return null;
  // Older UI converters copied award totals into price. They are identifiable
  // on award-source rows; opening saved work must not perpetuate that estimate.
  if (!observations.length && !product.price_provider_id && /usaspending|sam(?:\.gov)?/i.test(product.evidence?.source || '')
    && amount !== null && finiteUnitAmount(product.specifications?.award_amount) === amount) return null;
  if (product.price_observations?.length && !observations.some(row => row.amount === amount
    && row.currency === 'USD' && row.currentness !== 'historical')) return null;
  if (amount === 0 && !observations.some(row => row.amount === 0 && row.currentness !== 'historical')
    && !publishedListingZero(product)) return null;
  return amount;
}

const priceDisplayAllowed = (product: PriceProduct): boolean =>
  !['quote_required', 'not_published', 'unknown'].includes(product.price_status || '');

const regionalObservations = (product: PriceProduct, region: string): Record<string, any>[] => {
  const observations = unitObservations(product).filter(row => row.region === region);
  const nonhistorical = observations.filter(row => row.currentness !== 'historical');
  return nonhistorical.length ? nonhistorical : observations;
};

const displayedObservations = (product: PriceProduct): Record<string, any>[] => {
  const conus = regionalObservations(product, 'CONUS');
  if (conus.length) return conus;
  let observations = unitObservations(product);
  if (product.price_status === 'published') {
    const applicable = finiteUnitAmount(product.price === undefined ? product.unit_price : product.price);
    if (applicable !== null) observations = observations.filter(row => row.amount === applicable && row.currentness !== 'historical'
      && (!product.price_region || row.region === product.price_region));
  }
  return observations;
};

const formatObservations = (observations: Record<string, any>[], tooltip = false): string => observations.map(row => {
    const region = row.region === 'CONUS' ? 'Continental U.S.' : row.region === 'OCONUS'
      ? `Outside continental U.S.${tooltip ? ' (OCONUS)' : ''}` : row.region;
    const price = row.amount.toLocaleString('en-US', { style: 'currency', currency: row.currency });
    const date = row.published_at || row.publication_date;
    const qualification = date ? ` · ${date} catalog price; confirm current pricing`
      : row.currentness !== 'current' ? ' · confirm current pricing' : '';
    return `${region ? `${region}: ` : ''}${price} ${row.unit}${qualification}`;
  }).filter((text, index, values) => values.indexOf(text) === index).join('\n');

const unambiguousObservation = (observations: Record<string, any>[]): Record<string, any> | null => {
  const prices = new Set(observations.map(row => `${row.amount}|${row.currency}|${row.unit}|${row.configuration || ''}`));
  return prices.size === 1 ? observations[0] : null;
};

export function productPriceDisplay(product: PriceProduct): string {
  if (!priceDisplayAllowed(product)) return '';
  const observations = displayedObservations(product);
  if (observations.length) {
    const observation = unambiguousObservation(observations);
    return observation ? observation.amount.toLocaleString('en-US', { style: 'currency', currency: observation.currency }) : '';
  }
  const price = productUnitPrice(product);
  return price !== null ? price.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : '';
}

/** Display preference only: this does not select a destination or a cost estimate. */
export function productDisplayUnitPrice(product: PriceProduct): number | null {
  if (!priceDisplayAllowed(product)) return null;
  const observations = displayedObservations(product);
  if (!observations.length) return productUnitPrice(product);
  if (observations.some(row => row.currency !== 'USD' || row.currentness === 'historical')) return null;
  return unambiguousObservation(observations)?.amount ?? null;
}

export function productOconusPriceDisplay(product: PriceProduct): string {
  if (!priceDisplayAllowed(product) || !regionalObservations(product, 'CONUS').length) return '';
  return formatObservations(regionalObservations(product, 'OCONUS'), true);
}

/** Qualifications belong in accessible details, not in the numeric column. */
export function productPriceTooltip(product: PriceProduct): string {
  if (!productPriceDisplay(product)) return '';
  return [formatObservations(displayedObservations(product), true), productOconusPriceDisplay(product),
    productPriceProviderCaption(product)].filter(Boolean).join('\n');
}

/** A current USD whole-item statistic, on the same display basis as the grid. */
export function summarizeDisplayedProductPrices<T extends PriceProduct>(
  items: readonly T[],
): { median: number | null; pricedCount: number; totalCount: number } {
  const prices = items.map(item => {
    const price = productDisplayUnitPrice(item);
    const observations = displayedObservations(item);
    // Scalar source prices retain their established whole-item convention.
    // Area/package prices require conversion before they can share a median.
    return observations.length && !observations.every(row => /^(each|ea\.?|item|unit)$/i.test(row.unit.trim())) ? null : price;
  }).filter((price): price is number => price !== null).sort((a, b) => a - b);
  const middle = Math.floor(prices.length / 2);
  return { median: !prices.length ? null : prices.length % 2 ? prices[middle] : (prices[middle - 1] + prices[middle]) / 2,
    pricedCount: prices.length, totalCount: items.length };
}

/** Stable ordering by the price the table actually shows, including verified zero. */
export function sortByDisplayedUnitPrice<T extends PriceProduct>(items: readonly T[]): T[] {
  return items.map((item, index) => ({ item, index, price: productDisplayUnitPrice(item) }))
    .sort((left, right) => left.price === null ? right.price === null ? left.index - right.index : 1
      : right.price === null ? -1 : left.price - right.price || left.index - right.index)
    .map(row => row.item);
}

/** A joined listing's published price must not be attributed to its PDF. */
export function productPriceProviderCaption(product: {
  price_provider_id?: string; evidence?: { provider_id?: string };
  source_evidence?: readonly Record<string, any>[];
}): string | null {
  const provider = product.price_provider_id;
  if (!provider || provider === product.evidence?.provider_id) return null;
  const label = provider === 'unicor_gsa_advantage' ? 'GSA Advantage published price'
    : provider === 'unicor_official_catalog' ? 'UNICOR official catalog published price' : null;
  if (!label) return null;
  const observation = product.source_evidence?.find(row => row.provider_id === provider);
  const date = observation?.evidence?.observed_at;
  return label + (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(date) && Number.isFinite(Date.parse(date))
    ? ` — observed ${date.slice(0, 10)}` : '');
}

/**
 * Summarize the usable product prices without changing the caller-owned
 * collection. This is intentionally separate from the display ordering so
 * consumers can present a transparent planning statistic over every row.
 */
export function summarizeProductPrices<T>(
  items: readonly T[],
  getPrice: (item: T) => unknown,
): { median: number | null; pricedCount: number; totalCount: number } {
  const prices = items
    .map(getPrice)
    .map(finitePositiveProductPrice)
    .filter((price): price is number => price !== null)
    .sort((a, b) => a - b);

  const pricedCount = prices.length;
  if (pricedCount === 0) {
    return { median: null, pricedCount, totalCount: items.length };
  }

  const middle = Math.floor(pricedCount / 2);
  const median = pricedCount % 2 === 0
    ? (prices[middle - 1] + prices[middle]) / 2
    : prices[middle];

  return { median, pricedCount, totalCount: items.length };
}

/**
 * Return a stable, cheapest-first display projection. Finite positive prices
 * are ordered ascending; missing, on-request, zero, negative, and malformed
 * prices retain their original relative order after every priced row.
 */
export function sortByUnitPrice<T>(
  items: readonly T[],
  getPrice: (item: T) => unknown,
): T[] {
  return items
    .map((item, originalIndex) => ({ item, originalIndex, price: finitePositiveProductPrice(getPrice(item)) }))
    .sort((a, b) => {
      if (a.price === null && b.price === null) return a.originalIndex - b.originalIndex;
      if (a.price === null) return 1;
      if (b.price === null) return -1;
      return a.price === b.price ? a.originalIndex - b.originalIndex : a.price - b.price;
    })
    .map(({ item }) => item);
}
